
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { ChartBar, DollarSign, Leaf, Banknote, Gauge, Home } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const About = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Mission Section */}
      <section className="w-full py-24 px-4">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-8">Our Mission</h1>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-10">
            <h2 className="text-2xl font-semibold text-emerald-600 mb-6">
              Simplifying Energy Management
            </h2>
            <p className="text-xl text-gray-700">
              At KOSI, we make energy simple. Our mission is to turn complex energy data into clear, actionable insights. We cut through the confusion of energy setups and billing, helping you avoid costly mistakes and missed savings.
            </p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-6 mb-10">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">
              With our AI, we provide the tools to:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="w-5 h-5 flex-shrink-0">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="ml-3 text-lg font-medium text-gray-800">
                  Save money
                </span>
              </div>
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="w-5 h-5 flex-shrink-0">
                  <Gauge className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="ml-3 text-lg font-medium text-gray-800">
                  Reduce emissions
                </span>
              </div>
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="w-5 h-5 flex-shrink-0">
                  <Home className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="ml-3 text-lg font-medium text-gray-800">
                  Boost comfort
                </span>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-emerald-600 mb-6">
              Our Commitment
            </h2>
            <p className="text-xl text-gray-700">
              Understanding energy is key to smarter choices and a sustainable future. At KOSI Energy, we're here to empower you with clarity and control—one smart decision at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="w-full bg-white py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-[#1F2937] text-center mb-20">
            Meet our Team
          </h2>
          <div className="flex flex-col md:flex-row justify-center md:gap-32 gap-16">
            <div className="text-center">
              <Avatar className="w-48 h-48 mx-auto mb-6">
                <AvatarImage 
                  src="/lovable-uploads/00d77efb-f377-42e9-ad54-65f637b78f2f.png" 
                  alt="Paul Staal"
                  className="object-cover"
                />
                <AvatarFallback>PS</AvatarFallback>
              </Avatar>
              <h3 className="text-2xl font-bold text-[#1F2937] mb-2">Paul Staal</h3>
              <p className="text-lg text-[#4B5563]">Co-Founder</p>
            </div>
            <div className="text-center">
              <Avatar className="w-48 h-48 mx-auto mb-6">
                <AvatarImage 
                  src="/lovable-uploads/a771fd57-c1a9-4b5a-9af4-1e4279ec8226.png" 
                  alt="Sam Watt"
                  className="object-cover object-[15%_85%]"
                />
                <AvatarFallback>SW</AvatarFallback>
              </Avatar>
              <h3 className="text-2xl font-bold text-[#1F2937] mb-2">Sam Watt</h3>
              <p className="text-lg text-[#4B5563]">Co-Founder</p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="w-full bg-[#F9FAFB] py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1F2937] text-center mb-16">
            Our Expertise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-lg flex items-center justify-center mb-6">
                <ChartBar className="w-6 h-6 text-[#059669]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">Energy Analysis</h3>
              <p className="text-[#4B5563]">
                Comprehensive analysis of energy consumption patterns and optimisation opportunities.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-lg flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-[#059669]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">Price Overview</h3>
              <p className="text-[#4B5563]">
                Real-time monitoring and forecasting of energy market prices and trends.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-lg flex items-center justify-center mb-6">
                <Leaf className="w-6 h-6 text-[#059669]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">Sustainability Planning</h3>
              <p className="text-[#4B5563]">
                Strategic planning for sustainable energy usage and environmental impact reduction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-[#059669] py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Optimise Your Energy Usage?
          </h2>
          <p className="text-[#ECFDF5] text-xl mb-8">
            Join thousands of satisfied users who are saving money whilst helping the environment.
          </p>
          <Button
            asChild
            className="bg-white text-[#059669] hover:bg-white/90 text-lg px-8 py-6 h-auto"
          >
            <Link to="/auth">Start your free Assessment Now</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
